const BFF_BASE   = import.meta.env.VITE_BFF_URL    ?? 'http://localhost:8081'
const BFF_API_KEY = import.meta.env.VITE_BFF_API_KEY ?? ''

export class BffError extends Error {
  constructor(public status: number, message: string) {
    super(message)
    this.name = 'BffError'
  }
}

// ── Token cache (memória — não persiste entre reloads, intencional) ──────────
let cachedToken: string | null = null
let tokenExpiry = 0

async function obterToken(): Promise<string> {
  // Reutiliza token se válido com 60s de margem
  if (cachedToken && Date.now() < tokenExpiry - 60_000) {
    return cachedToken
  }

  const res = await fetch(`${BFF_BASE}/auth/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ api_key: BFF_API_KEY }),
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new BffError(res.status, body?.erro ?? 'Falha ao autenticar com o servidor')
  }

  const data = (await res.json()) as { token: string; expires_in: number }
  cachedToken  = data.token
  tokenExpiry  = Date.now() + data.expires_in * 1_000
  return cachedToken
}

// ── Requisição autenticada com retry automático em caso de 401 ───────────────
async function request<T>(method: 'GET' | 'POST', path: string, body?: unknown): Promise<T> {
  const executar = async (token: string) =>
    fetch(`${BFF_BASE}${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })

  let token = await obterToken()
  let res   = await executar(token)

  // Token rejeitado pelo servidor → invalida cache e tenta uma vez mais
  if (res.status === 401) {
    cachedToken = null
    tokenExpiry = 0
    token = await obterToken()
    res   = await executar(token)
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new BffError(res.status, err?.erro ?? `Erro ${res.status}`)
  }

  return res.json() as Promise<T>
}

export const bff = {
  get:  <T>(path: string)              => request<T>('GET',  path),
  post: <T>(path: string, body: unknown) => request<T>('POST', path, body),
}
